import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2,
  Save,
  Play,
  Archive,
  ArchiveRestore,
  Trash2,
  Printer,
  FileSpreadsheet,
  Download,
  Upload,
} from 'lucide-react';
import type { QuotaPolicy } from '@cca/xero-shared';
import {
  api,
  type QuotaPolicySummary,
  type QuotaRunResponse,
  type ExclusionCategory,
} from '../../api/client';
import { PolicyForm, printQuotaRun, downloadRunXlsx, downloadRunCsv } from '@cca/quota-ui';
import { ScheduleTable } from './ScheduleTable';
import { defaultPolicy } from './policyDefaults';
import { describePolicy } from './policyDescribe';

// Policy editor + run/results. The flow is tinker → draft → save: the registrar
// adjusts the settings, runs a DRAFT (a what-if, nothing stored) to see the
// numbers, and only then saves it as a policy. Saving auto-assigns the next
// version (per Start Year, e.g. 2026-1) and stores the run for the record.

const THIS_YEAR = new Date().getFullYear();

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text2)',
  display: 'block',
  marginBottom: 2,
};

export function PolicyRunTab() {
  const [policies, setPolicies] = useState<QuotaPolicySummary[]>([]);
  const [categories, setCategories] = useState<ExclusionCategory[]>([]);
  const [policy, setPolicy] = useState<QuotaPolicy>(() => defaultPolicy(THIS_YEAR));
  // Invoice Year (the year the quota is charged) is carried on the policy and
  // edited in PolicyForm; runs/drafts use it as the assessment_year.
  const invoiceYear = policy.effective_assessment_year ?? THIS_YEAR;
  const [run, setRun] = useState<QuotaRunResponse | null>(null);
  const [runMode, setRunMode] = useState<'draft' | 'stored' | null>(null);
  // Note shown on the saved policy — pre-filled from the settings, then editable.
  // It tracks the settings until the registrar types into it (noteDirty), after
  // which their wording is left alone.
  const [note, setNote] = useState('');
  const [noteDirty, setNoteDirty] = useState(false);
  // Editable note on the loaded saved policy — autosaved (debounced).
  const [savedNote, setSavedNote] = useState('');
  const [noteSaveStatus, setNoteSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  // Status line for the "Push to Quota Model" populate control.
  const [pushMsg, setPushMsg] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadPolicies = useCallback(async () => {
    try {
      setPolicies(await api.quotaPolicies(showArchived));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load policies');
    }
  }, [showArchived]);

  useEffect(() => {
    void loadPolicies();
  }, [loadPolicies]);

  useEffect(() => {
    void api
      .quotaCategories()
      .then((cs) => setCategories(cs.filter((c) => c.active)))
      .catch(() => null);
  }, []);

  // Keep the note in step with the settings until the registrar edits it.
  useEffect(() => {
    if (!noteDirty) setNote(describePolicy(policy));
  }, [policy, noteDirty]);

  const loadPolicy = async (version: string) => {
    if (!version) return;
    setError(null);
    setNotice(null);
    try {
      const p = await api.quotaPolicy(version);
      setPolicy(p);
      setRun(null);
      setRunMode(null);
      setNote(p.label ?? describePolicy(p));
      setNoteDirty(true);
      setSavedNote(p.label ?? '');
      setNoteSaveStatus('idle');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load policy');
    }
  };

  // Edit the loaded policy's note — debounced autosave (notes are metadata, the
  // policy parameters and stored results are untouched).
  const onNoteChange = (v: string) => {
    setSavedNote(v);
    if (!known) return;
    const version = policy.policy_version;
    setNoteSaveStatus('saving');
    if (noteTimer.current) clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => {
      void api
        .quotaSetPolicyNote(version, v.trim())
        .then(() => {
          setNoteSaveStatus('saved');
          setPolicies((prev) =>
            prev.map((p) => (p.policy_version === version ? { ...p, label: v.trim() } : p)),
          );
        })
        .catch((e) => setError(e instanceof Error ? e.message : 'note save failed'));
    }, 700);
  };

  useEffect(() => () => void (noteTimer.current && clearTimeout(noteTimer.current)), []);

  const selected = policies.find((p) => p.policy_version === policy.policy_version);
  const known = selected !== undefined;
  const isArchived = selected?.archived ?? false;

  const newDraft = () => {
    setPolicy(defaultPolicy(THIS_YEAR));
    setRun(null);
    setRunMode(null);
    setNotice(null);
    setError(null);
    setNoteDirty(false); // note refills from the fresh settings
    setSavedNote('');
    setNoteSaveStatus('idle');
  };

  // Draft run: compute across every church WITHOUT storing (what-if).
  const runDraft = async () => {
    setBusy('draft');
    setError(null);
    setNotice(null);
    try {
      const result = await api.quotaWhatif(policy, invoiceYear);
      setRun(result);
      setRunMode('draft');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'draft run failed');
    } finally {
      setBusy(null);
    }
  };

  // Archive (soft-delete) or restore the currently-loaded saved policy. Archiving
  // only hides it — the config and stored results are kept, so it's reversible.
  const archiveSelected = async () => {
    if (!known) return;
    const version = policy.policy_version;
    setBusy('archive');
    setError(null);
    setNotice(null);
    try {
      await api.quotaArchivePolicy(version);
      await loadPolicies();
      if (!showArchived) newDraft();
      setNotice(`Archived ${version}. Tick "Show archived" to restore it.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'archive failed');
    } finally {
      setBusy(null);
    }
  };

  const restoreSelected = async () => {
    if (!known) return;
    const version = policy.policy_version;
    setBusy('archive');
    setError(null);
    setNotice(null);
    try {
      await api.quotaRestorePolicy(version);
      await loadPolicies();
      setNotice(`Restored ${version}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'restore failed');
    } finally {
      setBusy(null);
    }
  };

  // Permanent delete — only offered for an archived policy, behind a confirm.
  const deletePermanently = async () => {
    if (!known || !isArchived) return;
    const version = policy.policy_version;
    if (
      !window.confirm(
        `Permanently delete ${version}? This removes the policy and its stored assessment results for good. This cannot be undone.`,
      )
    )
      return;
    setBusy('delete');
    setError(null);
    setNotice(null);
    try {
      await api.quotaDeleteArchivedPolicy(version);
      await loadPolicies();
      newDraft();
      setNotice(`Permanently deleted ${version}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'delete failed');
    } finally {
      setBusy(null);
    }
  };

  // Save: mint the next version (server auto-numbers), then store the run.
  const saveAsPolicy = async () => {
    setBusy('save');
    setError(null);
    setNotice(null);
    try {
      const toSave: QuotaPolicy = { ...policy, label: note.trim(), policy_version: '' };
      const { policy_version } = await api.quotaSavePolicy(toSave);
      const stored = await api.quotaRun(policy_version, invoiceYear);
      setPolicy({ ...policy, policy_version, label: note.trim() });
      setNoteDirty(true);
      setSavedNote(note.trim());
      setNoteSaveStatus('idle');
      setRun(stored);
      setRunMode('stored');
      await loadPolicies();
      setNotice(`Saved as ${policy_version}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-end',
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <label>
          <span style={labelStyle}>Load saved policy</span>
          <select
            className="input"
            value={known ? policy.policy_version : ''}
            style={{ width: 200 }}
            onChange={(e) => (e.target.value ? void loadPolicy(e.target.value) : newDraft())}
          >
            <option value="">— new draft —</option>
            {policies.map((p) => (
              <option key={p.policy_version} value={p.policy_version}>
                {p.policy_version}
                {p.archived ? ' (archived)' : ''}
              </option>
            ))}
          </select>
        </label>
        <button
          className="btn btn-ghost btn-sm"
          disabled={!known || busy !== null}
          title={
            !known
              ? 'Load a saved policy first'
              : isArchived
                ? 'Restore this policy'
                : 'Archive (hide) this policy'
          }
          onClick={() => void (isArchived ? restoreSelected() : archiveSelected())}
        >
          {busy === 'archive' ? (
            <Loader2 className="animate-spin" size={14} />
          ) : isArchived ? (
            <ArchiveRestore size={14} />
          ) : (
            <Archive size={14} />
          )}
          &nbsp;{isArchived ? 'Restore' : 'Archive'}
        </button>
        {isArchived && (
          <button
            className="btn btn-ghost btn-sm"
            disabled={busy !== null}
            title="Permanently delete this archived policy"
            style={{ color: 'var(--red)' }}
            onClick={() => void deletePermanently()}
          >
            {busy === 'delete' ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              <Trash2 size={14} />
            )}
            &nbsp;Delete
          </button>
        )}
        <label style={{ flex: 1, minWidth: 200 }}>
          <span style={labelStyle}>
            Note on this policy
            {known && noteSaveStatus === 'saving' && (
              <span style={{ color: 'var(--text2)' }}> · saving…</span>
            )}
            {known && noteSaveStatus === 'saved' && (
              <span style={{ color: 'var(--text2)' }}> · saved ✓</span>
            )}
          </span>
          <input
            className="input"
            value={known ? savedNote : ''}
            disabled={!known}
            placeholder={known ? 'Add a note…' : 'Load a saved policy to edit its note'}
            style={{ width: '100%' }}
            onChange={(e) => onNoteChange(e.target.value)}
          />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, paddingBottom: 8 }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span style={{ fontSize: 12 }}>Show archived</span>
        </label>
      </div>

      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      {notice && (
        <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 10 }}>{notice}</div>
      )}

      <PolicyForm policy={policy} onChange={setPolicy} categories={categories} hideVersion />

      <label style={{ display: 'block', marginTop: 8, marginBottom: 10 }}>
        <span style={labelStyle}>Note (pre-filled from the settings — edit to add your own)</span>
        <input
          className="input"
          value={note}
          style={{ width: '100%', maxWidth: 560 }}
          onChange={(e) => {
            setNote(e.target.value);
            setNoteDirty(true);
          }}
        />
      </label>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <button className="btn btn-ghost" disabled={busy !== null} onClick={() => void runDraft()}>
          {busy === 'draft' ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />}
          &nbsp;Run draft
        </button>
        <button className="btn" disabled={busy !== null} onClick={() => void saveAsPolicy()}>
          {busy === 'save' ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
          &nbsp;Save as policy
        </button>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-ghost"
          title="Copy the current quota data to the public Quota Model consultation app (quota-calculator.confessinganglicans.nz). One-directional; runs nightly too."
          disabled={busy !== null}
          onClick={() =>
            void (async () => {
              setBusy('push');
              setPushMsg('');
              try {
                const r = await api.quotaPushToModel();
                setPushMsg(
                  `✓ Pushed ${r.churches} churches / ${r.financials} years to the Quota Model.`,
                );
              } catch (e) {
                setPushMsg(e instanceof Error ? e.message : 'Push failed');
              } finally {
                setBusy(null);
              }
            })()
          }
        >
          {busy === 'push' ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
          &nbsp;Push to Quota Model
        </button>
      </div>
      {pushMsg && <p style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>{pushMsg}</p>}

      {run && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              marginBottom: 8,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {runMode === 'stored' ? (
                <>
                  Schedule — {run.policy_version} · Invoice year {run.assessment_year}{' '}
                  <span style={{ fontWeight: 400, color: 'var(--text2)' }}>(stored)</span>
                </>
              ) : (
                <>
                  Schedule — Invoice year {run.assessment_year}{' '}
                  <span style={{ fontWeight: 400, color: 'var(--text2)' }}>
                    (draft — not saved)
                  </span>
                </>
              )}
            </div>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-ghost btn-sm"
              title="Print / save as PDF"
              onClick={() =>
                printQuotaRun({
                  run,
                  policy,
                  categories,
                  mode: runMode ?? 'draft',
                  attribution: 'the CCAANZ Finance Portal',
                })
              }
            >
              <Printer size={14} />
              &nbsp;Print / PDF
            </button>
            <button
              className="btn btn-ghost btn-sm"
              title="Download as Excel"
              onClick={() => void downloadRunXlsx(run, policy, categories)}
            >
              <FileSpreadsheet size={14} />
              &nbsp;Excel
            </button>
            <button
              className="btn btn-ghost btn-sm"
              title="Download as CSV"
              onClick={() => downloadRunCsv(run, policy, categories)}
            >
              <Download size={14} />
              &nbsp;CSV
            </button>
          </div>
          <ScheduleTable run={run} />
        </div>
      )}
    </div>
  );
}
